// SkuWizard — scoped template picker with category / name search.
//
// SCOPING REVERSAL · 20260610
// The earlier rule (20260721) was "品番 templates are PUBLIC — no scoping by
// app, department or type; any user can issue from any rule". That is reversed
// here, deliberately. It predates having six ledgers with colliding prefixes,
// and the concrete cost of "public" was the EQP leak: 設備 (EQP) rules showed up
// in the IT資産 / オフィス備品 / 技術資産 pickers, where picking one gives an
// asset a number the 設備台帳 owns.
// Now: HARD FILTER — each mount point passes `defaultApp` and only that scope's
// rules are listed. A rule with a blank app_code still matches every scope; that
// is the compatibility path and the intended escape valve, so there is no
// "show all" control (a UI override whose only function is to permit the wrong
// choice is not worth the affordance).
// Separately, rules whose prefix resolves to an equipment-ledger prefix (EQP)
// are always listed but never selectable — 設備台帳 owns that counter.
//
// Props:
//   trigger?          — element that opens the wizard
//   open?, onOpenChange? — controlled mode
//   defaultApp        — sku_apps.code scope for this mount point. Blank = unscoped.
//   onIssued(sku)     — callback when a SKU is issued
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Hash, Sparkle, LockSimple, ArrowsClockwise, ArrowRight, MagnifyingGlass, X,
} from '@phosphor-icons/react';
import { SkuGeneratorDialog } from '@/components/SkuGeneratorDialog';
import { skuMajorTitle, groupTemplatesByName, skuTemplatePrefix } from '@/lib/skuDisplay';
import { useLedgerCounters, ledgerLockFor } from '@/lib/ledgerPrefixes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Segment kinds that resolve automatically at issue time (no user input).
// Kept in sync with routers/sku.py::_build_sku.
const AUTO_KINDS = new Set(['literal', 'year2', 'year4', 'month', 'counter']);

export function SkuWizard({
  trigger, open: controlledOpen, onOpenChange, onIssued,
  defaultApp = '', defaultDept,
  currentSku = '', currentTemplateCode = '',
  // 20260610 の HARD FILTER は「ほかの台帳のルールを誤って選ぶ」事故を防ぐためのもの。
  // オーナーの判断（2026-06）で、登録フォームでは**全ルールから選べる**ようにする。
  // ただし並びは「この台帳のルール」を先に、残りを後ろに畳んで出す。
  // `EQP` などの台帳所有プレフィックスは今までどおり選べない（カウンターが二重になる）。
  listAll = false,
  // §8.10 自分の台帳の 部署/種類 ルール（技術資産→`ENG/CSM`・IT資産→`IT/EQP`）は
  // 選べるようにする。番号は共通採番の1本のカウンターから出るので二重採番にならない。
  // `sharedSeries`: [{ prefix, label, next }]・`deptLabel`: 'ENG' / 'IT'。
  sharedSeries = [], deptLabel = '',
  // 既定ルールを決めるだけの用途（番号は発番しない）。
  pickOnly = false,
  // 2026-10-28 · 品番グループを選んである画面では、その記号のルールだけを先に出す
  // （IT・SNS まで全部並ぶと、どれが自分のものか分からない）。
  preferPrefix = '',
}) {
  // defaultDept is accepted for backward-compat; scoping is by app only.
  void defaultDept;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = isControlled ? onOpenChange : setUncontrolledOpen;

  const [templates, setTemplates] = useState([]);
  const ledger = useLedgerCounters();
  const [issuing, setIssuing] = useState(null); // template code being issued
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedTemplate, setAdvancedTemplate] = useState(null);
  const [search, setSearch] = useState('');

  // Load this scope's templates on open. The API treats blank/unset app_code on
  // a rule as "matches every scope", so legacy unscoped rules keep working.
  const loadTemplates = useCallback(async () => {
    if (!open) return;
    try {
      const r = await axios.get(`${API}/sku/templates`, {
        params: (defaultApp && !listAll) ? { app_code: defaultApp } : {},
        withCredentials: true,
      });
      setTemplates(r.data || []);
    } catch { /* silent */ }
  }, [open, defaultApp, listAll]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { if (open) setSearch(''); }, [open]);

  // Search filter — case-insensitive prefix + substring match against name,
  // code, category, and sample.
  const [onlyPrefix, setOnlyPrefix] = useState(true);
  useEffect(() => { if (open) setOnlyPrefix(true); }, [open]);
  const prefixMatches = useMemo(() => (preferPrefix
    ? templates.filter((t) => skuTemplatePrefix(t) === String(preferPrefix).toUpperCase())
    : []), [templates, preferPrefix]);
  const scoped = (preferPrefix && onlyPrefix && prefixMatches.length)
    ? prefixMatches : templates;
  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((t) => {
      const hay = `${t.name} ${t.code} ${t.category || ''} ${t.sample || ''} ${t.department || ''} ${skuMajorTitle(t)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [scoped, search]);

  // 20260801 · Group by rule NAME; each card's main title is the 大分類.
  const templatesByName = useMemo(
    () => groupTemplatesByName(filteredTemplates),
    [filteredTemplates],
  );

  // Unique rule names — used to show quick-filter chips above the list.
  const allNames = useMemo(() => {
    const s = new Set();
    for (const t of scoped) { const n = (t.name || '').trim(); if (n) s.add(n); }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [scoped]);

  // Determine if a template is "one-tap" (all non-auto segments have a preset value).
  const missingCount = (tpl) => {
    const presets = tpl.values || {};
    return (tpl.segments || [])
      .filter((s) => !AUTO_KINDS.has(s.kind))
      .filter((s) => !presets[s.key])
      .length;
  };

  // 2026-11-03 · 品番グループはサーバが解決した値をそのまま渡す（全登録フォームが
  // これを使う。画面ごとに接頭辞から推測しない — 正本は services/sku_group_link.py）。
  const groupMeta = (tpl) => ({
    sku_group_code: tpl?.sku_group_code || '',
    sku_group_name: tpl?.sku_group_name || '',
    sku_group_category_id: tpl?.sku_group_category_id || '',
    sku_group_missing: !!tpl?.sku_group_missing,
  });

  // §8.10 このルールが「自分の台帳の共通採番ルール」か（選べる・番号は共通採番から）。
  const sharedPick = useCallback((tpl) => {
    if (!sharedSeries.length || !deptLabel) return null;
    const prefix = skuTemplatePrefix(tpl);
    const row = sharedSeries.find((s) => s.prefix === prefix);
    if (!row) return null;
    const lits = (tpl.segments || [])
      .filter((s) => s?.kind === 'literal').map((s) => String(s.value ?? '')).join('')
      || String(tpl.sample || '');
    const m = lits.trim().match(/^([A-Za-z]{2,8})\s*\/\s*(EQP|CSM|FAB)/i);
    if (!m || m[1].toUpperCase() !== deptLabel.toUpperCase()) return null;
    return row;
  }, [sharedSeries, deptLabel]);

  const pickTemplate = async (tpl) => {
    const shared = sharedPick(tpl);
    const lock = ledgerLockFor(tpl, ledger);
    if (lock && !shared) {
      toast.info(`${lock.note} — このルールからは発番できません`);
      return;
    }
    if (pickOnly) {
      onIssued?.('', { asset_kind: tpl.asset_kind || '', template_code: tpl.code,
        template_name: tpl.name || '', shared_prefix: shared?.prefix || '',
        ...groupMeta(tpl) });
      setOpen(false);
      return;
    }
    if (shared) {
      // 番号は共通採番から保存時に出る。ここでは「次に出る番号」を見せるだけ。
      onIssued?.(shared.next || '', {
        asset_kind: tpl.asset_kind || '', template_code: tpl.code,
        template_name: tpl.name || '', shared_prefix: shared.prefix, from_shared: true,
        ...groupMeta(tpl),
      });
      toast.success(`共通採番から発番します（保存時に確定）: ${shared.next || shared.prefix}`);
      setOpen(false);
      return;
    }
    // 同じ品番テンプレートを再選択した場合は再発番せず既存 SKU を再利用
    // (deselect → reselect で番号が毎回増える問題を防止)
    if (currentSku && currentTemplateCode && tpl.code === currentTemplateCode) {
      toast.info(`既に発番済みの品番「${currentSku}」を使用します`);
      onIssued?.(currentSku, { asset_kind: tpl.asset_kind || '', template_code: tpl.code,
        template_name: tpl.name || '', ...groupMeta(tpl) });
      setOpen(false);
      return;
    }
    const missing = missingCount(tpl);
    if (missing === 0) {
      setIssuing(tpl.code);
      try {
        // プレビューのみ — 正式発番は保存時（キャンセル時は番号を消費しない）
        const r = await axios.post(
          `${API}/sku/preview`,
          { template_id: tpl.id, values: tpl.values || {} },
          { withCredentials: true },
        );
        toast.success(`発番予定: ${r.data.sku}（保存時に確定・キャンセル時は消費されません）`);
        onIssued?.(r.data.sku, {
          ...groupMeta(tpl),
          asset_kind: tpl.asset_kind || '', template_code: tpl.code,
          pending: { template_id: tpl.id, values: tpl.values || {} },
        });
        setOpen(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || '採番プレビューに失敗しました');
      } finally {
        setIssuing(null);
      }
    } else {
      // Fallback — open detailed dialog with template pre-applied
      setAdvancedTemplate(tpl.code);
      setAdvancedOpen(true);
    }
  };

  const openBlankAdvanced = () => {
    setAdvancedTemplate(null);
    setAdvancedOpen(true);
  };

  const handleAdvancedIssued = (sku, meta) => {
    const tpl = templates.find((t) => t.code === advancedTemplate);
    onIssued?.(sku, {
      asset_kind: tpl?.asset_kind || '', template_code: tpl?.code || '',
      template_name: tpl?.name || '', pending: meta?.pending || null,
      ...groupMeta(tpl),
    });
    setOpen(false);
  };

  const dialogContent = (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sku-wizard-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Hash weight="duotone" className="text-blue-600" size={22} />
          <span>品番自動採番</span>
        </DialogTitle>
        <DialogDescription>
          品番ルールを選んでください。ワンタップで発番できます（分類・キーワード検索も可）。
        </DialogDescription>
      </DialogHeader>

      {/* Search + category chips */}
      <div className="space-y-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ルール名・大分類・コード・サンプルで検索…"
            className="pl-9 pr-9 h-10"
            data-testid="sku-wizard-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              data-testid="sku-wizard-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {preferPrefix && prefixMatches.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="sku-wizard-prefix-scope">
            <span className="text-[11px] text-zinc-600">
              {onlyPrefix
                ? `品番グループ「${preferPrefix}」のルールだけ表示しています（${prefixMatches.length}件）`
                : 'すべてのルールを表示しています'}
            </span>
            <button type="button" onClick={() => setOnlyPrefix((v) => !v)}
              className="text-[11px] font-semibold text-blue-600 underline hover:text-blue-700"
              data-testid="sku-wizard-prefix-toggle">
              {onlyPrefix ? 'すべてのルールを見る' : `${preferPrefix} のルールだけに戻す`}
            </button>
          </div>
        )}
        {allNames.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="sku-wizard-name-chips">
            {allNames.slice(0, 12).map((name) => {
              const active = search.toLowerCase() === name.toLowerCase();
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setSearch(active ? '' : name)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                  }`}
                  data-testid={`sku-wizard-name-chip-${name}`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Templates */}
      {templates.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/40 p-6 text-center">
          <div className="text-sm text-zinc-600">
            まだこの画面で使える品番ルールがありません。<br />
            <span className="text-xs text-zinc-500">
              <a href="/settings/sku-master" className="underline text-blue-600 mx-1">設定 → 品番マスター</a>
              から作成し、対象アプリをこの画面に合わせてください（「すべてのアプリ」でも使えます）。
            </span>
          </div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-200 p-6 text-center">
          <div className="text-sm text-zinc-500">
            「{search}」に一致するルールが見つかりません
          </div>
          <button
            type="button"
            onClick={() => setSearch('')}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
          >
            検索条件をクリア
          </button>
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {templatesByName.map((g) => (
            <div key={g.name || '__flat__'} data-testid={`sku-wizard-group-${g.name}`}>
              {g.name && (
                <div className="text-[11px] font-bold text-zinc-600 mb-1 sticky top-0 bg-white pt-1">
                  {g.name}
                </div>
              )}
              <div className="space-y-1.5">
                {g.items.map((tpl) => {
                  const missing = missingCount(tpl);
                  const shared = sharedPick(tpl);
                  const lock = ledgerLockFor(tpl, ledger);
                  const ledgerOwned = !!lock && !shared;
                  const oneTap = (missing === 0 && !ledgerOwned) || !!shared;
                  return (
                    <button
                      key={tpl.code}
                      type="button"
                      onClick={() => pickTemplate(tpl)}
                      disabled={issuing === tpl.code || ledgerOwned}
                      aria-disabled={ledgerOwned}
                      data-testid={`sku-wizard-tpl-${tpl.code}`}
                      className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 transition-all ${
                        ledgerOwned
                          ? 'border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed'
                          : oneTap
                            ? 'border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm'
                            : 'border-zinc-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${oneTap ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        {issuing === tpl.code ? (
                          <ArrowsClockwise className="animate-spin text-zinc-500" size={16} />
                        ) : oneTap ? (
                          <Sparkle weight="fill" className="text-emerald-600" size={16} />
                        ) : (
                          <LockSimple weight="fill" className="text-zinc-500" size={16} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-bold truncate ${ledgerOwned ? 'text-zinc-500' : 'text-zinc-800'}`}>{skuMajorTitle(tpl)}</div>
                        <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                          <span className="font-mono">{tpl.code}</span>
                          {tpl.asset_kind === 'consumable' && (
                            <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold">消耗品</span>
                          )}
                          {tpl.asset_kind === 'durable' && (
                            <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[10px] font-semibold">資産</span>
                          )}
                          {tpl.department && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{tpl.department}</span>
                          )}
                          {tpl.sample && (
                            <span className="font-mono text-blue-600" title="サンプル">→ {tpl.sample}</span>
                          )}
                          {ledgerOwned
                            ? (
                              <span
                                className="px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 text-[10px] font-semibold"
                                data-testid={`sku-wizard-tpl-locked-${tpl.code}`}
                              >
                                {lock.note}
                              </span>
                            )
                            : shared
                              ? (
                                <span
                                  className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 text-[10px] font-semibold"
                                  data-testid={`sku-wizard-tpl-shared-${tpl.code}`}
                                >
                                  共通採番から発番{shared.next ? ` 次 ${shared.next}` : ''}
                                </span>
                              )
                              : oneTap
                                ? <span className="text-emerald-600">ワンタップ発番可</span>
                                : <span className="text-zinc-500">{missing}項目の入力が必要</span>}
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-zinc-400 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="pt-2 flex justify-center">
            <button
              onClick={openBlankAdvanced}
              className="text-xs text-zinc-500 hover:text-blue-600 underline"
              data-testid="sku-wizard-fallback-link"
            >
              詳細ダイアログを開く
            </button>
          </div>
        </div>
      )}

      {/* Detailed dialog (fallback + incomplete-template) */}
      <SkuGeneratorDialog
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        initialTemplateCode={advancedTemplate}
        deferred
        defaultApp={defaultApp}
        onIssued={handleAdvancedIssued}
      />
    </DialogContent>
  );

  if (isControlled) {
    return <Dialog open={open} onOpenChange={setOpen}>{dialogContent}</Dialog>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      {dialogContent}
    </Dialog>
  );
}

export default SkuWizard;
